import type { ReactNode } from "react";

/**
 * The top of a screen: what this page is, one line on what it is for, and the page's primary
 * action pinned right.
 *
 * Screens had been opening straight into their content, or with a bare `<h2>`, so nothing told you
 * where you were or what you could do here — the primary action sat wherever it happened to be
 * written. One header component means every screen answers those two questions in the same place.
 */
export const PageHeader = ({
  title,
  description,
  actions,
  back,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** A labelled way back, for screens reached from another screen rather than the sidebar. */
  back?: { label: string; onClick: () => void };
}): JSX.Element => (
  <header className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      {back && (
        <button
          type="button"
          onClick={back.onClick}
          className="mb-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          ← {back.label}
        </button>
      )}
      <h2 className="font-heading text-xl font-semibold tracking-tight">{title}</h2>
      {description && (
        <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </header>
);
